import { useCallback, useMemo, useRef } from "react";
import { appInsightsEvents, trackEvent, useAppStore } from "../../common";

export type WebAgentErrorScope =
  | "tokenExchange"
  | "directLineConnection"
  | "init"
  | "tokenRefresh"
  | "send";

export const useWebAgentTelemetry = () => {
  const appInsights = useAppStore((state) => state.appInsights);
  const context = useAppStore((state) => state.context);
  const conversationIdRef = useRef<string | undefined>(undefined);

  const setConversationId = useCallback((id: string | undefined) => {
    conversationIdRef.current = id;
  }, []);

  const getCustomProperties = useCallback(
    (extra?: Record<string, unknown>) => ({
      ...(conversationIdRef.current ? { conversationId: conversationIdRef.current } : {}),
      ...(extra ?? {}),
    }),
    [],
  );

  const error = useCallback(
    (scope: WebAgentErrorScope, err: unknown) => {
      const exception = err instanceof Error ? err : new Error(String(err));
      appInsights.trackException(
        { exception },
        { component: "WebAgent", scope, ...getCustomProperties() },
      );
    },
    [appInsights, getCustomProperties],
  );

  const opened = useCallback(
    () =>
      trackEvent(
        appInsights,
        appInsightsEvents.Usage.Actions.WebAgent.Opened,
        context,
        getCustomProperties(),
      ),
    [appInsights, context, getCustomProperties],
  );
  const closed = useCallback(
    () =>
      trackEvent(
        appInsights,
        appInsightsEvents.Usage.Actions.WebAgent.Closed,
        context,
        getCustomProperties(),
      ),
    [appInsights, context, getCustomProperties],
  );
  const messageSent = useCallback(
    (properties: { messageLength: number }) =>
      trackEvent(
        appInsights,
        appInsightsEvents.Usage.Actions.WebAgent.MessageSent,
        context,
        getCustomProperties(properties),
      ),
    [appInsights, context, getCustomProperties],
  );
  const newChat = useCallback(
    () =>
      trackEvent(
        appInsights,
        appInsightsEvents.Usage.Actions.WebAgent.NewChat,
        context,
        getCustomProperties(),
      ),
    [appInsights, context, getCustomProperties],
  );
  const retry = useCallback(
    () =>
      trackEvent(
        appInsights,
        appInsightsEvents.Usage.Actions.WebAgent.Retry,
        context,
        getCustomProperties(),
      ),
    [appInsights, context, getCustomProperties],
  );
  const resolved = useCallback(
    () =>
      trackEvent(
        appInsights,
        appInsightsEvents.Usage.Actions.WebAgent.Resolved,
        context,
        getCustomProperties(),
      ),
    [appInsights, context, getCustomProperties],
  );
  const abandoned = useCallback(
    () =>
      trackEvent(
        appInsights,
        appInsightsEvents.Usage.Actions.WebAgent.Abandoned,
        context,
        getCustomProperties(),
      ),
    [appInsights, context, getCustomProperties],
  );
  const trackLatency = useCallback(
    (latencyMs: number) => {
      appInsights.trackMetric(
        { name: appInsightsEvents.Performance.WebAgent.ResponseLatencyMs, average: latencyMs },
        getCustomProperties(),
      );
    },
    [appInsights, getCustomProperties],
  );

  return useMemo(
    () => ({
      error,
      opened,
      closed,
      messageSent,
      newChat,
      retry,
      resolved,
      abandoned,
      trackLatency,
      setConversationId,
    }),
    [
      error,
      opened,
      closed,
      messageSent,
      newChat,
      retry,
      resolved,
      abandoned,
      trackLatency,
      setConversationId,
    ],
  );
};
